import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as CloseIcon } from '../../icons/close-x-small.svg';
import './bottom-sheet.scss';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** Extra icon buttons rendered between the title and the close button. */
  headerActions?: React.ReactNode;
  /** Replaces the title block (e.g. an inline rename form); the X stays. */
  header?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Transient confirmation shown as a pill floating just above the sheet. */
  toast?: string | null;
}

/**
 * Mobile bottom sheet: slides up over a blurred backdrop, closes on the X,
 * on tapping the backdrop above it, or on Escape. Bottom padding includes the
 * safe-area inset so the primary action stays clear of Safari's toolbar.
 */
const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onClose,
  title,
  subtitle,
  headerActions,
  header,
  children,
  className,
  toast,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className='bottom-sheet-overlay' onMouseDown={onClose}>
      {toast && (
        <div className='bottom-sheet-toast' role='status'>
          {toast}
        </div>
      )}
      <div
        className={`bottom-sheet${className ? ` ${className}` : ''}`}
        role='dialog'
        aria-modal='true'
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className='bottom-sheet-header'>
          {header ?? (
            <div className='bottom-sheet-heading'>
              <h3 className='bottom-sheet-title'>{title}</h3>
              {subtitle && <p className='bottom-sheet-subtitle'>{subtitle}</p>}
            </div>
          )}
          <div className='bottom-sheet-header-actions'>
            {headerActions}
            <button
              type='button'
              className='bottom-sheet-close'
              aria-label='Close'
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className='bottom-sheet-body'>{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default BottomSheet;
